import { assertStaffCapability } from "./authorization.js";
import { CATALOG_MEDIA_ATTACH_WINDOW_MS, CATALOG_UPLOAD_MAX_BYTES, catalogMediaPrefix, catalogUploadContentTypes, parseCatalogMediaObjectPath, uuidV7Instant, } from "./catalog.js";
import { validateCatalogCategories, validateCatalogCoreFields, validateCatalogExternalRefs, validateCatalogFulfilment, validateCatalogLocation, validateCatalogMedia, validateCatalogPeople, validateCatalogSubtype, validateCatalogTags, } from "./catalog-draft.js";
import { DomainValidationError } from "./identifiers.js";
import { checkExpectedVersion, isMember, violation } from "./transition-support.js";
export const catalogContentFields = Object.freeze([
    "name",
    "slug",
    "shortDescription",
    "description",
    "addressText",
    "locality",
    "region",
    "countryCode",
    "latitude",
    "longitude",
    "timeZone",
    "fulfilmentMode",
    "externalUrl",
    "editorialPriority",
]);
/** `catalog.edit`, an editable status and a current version: the three checks every edit command makes first. */
function gate(item, input) {
    assertStaffCapability(input.actor, "catalog.edit", input.environment);
    if (item.status === "ARCHIVED")
        throw violation("catalog.archived");
    checkExpectedVersion(input.actor, input.expectedVersion, item.version, "catalog.version_conflict");
}
const sameValue = (a, b) => a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : a === b;
const TEXT_FIELDS = ["name", "shortDescription", "description", "addressText"];
const LOCATION_FIELDS = [
    "locality",
    "region",
    "countryCode",
    "latitude",
    "longitude",
    "timeZone",
];
/**
 * A details edit: a partial patch of the item's common fields. Each touched value is judged by the
 * rule of the draft (the same functions), the merged item is judged for the fulfilment mode and the
 * link that goes with it, and a patch that changes nothing is refused. The slug is frozen after the
 * first publication (`catalog.slug_frozen`). A fulfilment change on a payment-grade item is refused
 * (`catalog.price_fulfilment_not_payable`: the price would stop being payable; the database CHECK is
 * the backstop). Whether a PUBLISHED item stays publishable is judged by the use case on the merged
 * aggregate (`catalog.edit_breaks_publication`).
 */
export function planCatalogDetailsEdit(input) {
    const { item } = input;
    gate(item, input);
    const raw = input.patch;
    if (typeof raw !== "object" || raw === null)
        throw new DomainValidationError("patch", "must be an object");
    for (const key of Object.keys(raw)) {
        if (!catalogContentFields.includes(key)) {
            throw new DomainValidationError("patch", "names a field that cannot be edited");
        }
    }
    const given = definedOnly(raw);
    const merged = { ...pick(item), ...given };
    const core = validateCatalogCoreFields(merged);
    const touchesFulfilment = "fulfilmentMode" in given || "externalUrl" in given;
    const fulfilment = touchesFulfilment
        ? validateCatalogFulfilment(merged.fulfilmentMode, merged.externalUrl)
        : { fulfilmentMode: item.fulfilmentMode, externalUrl: item.externalUrl };
    const touchesLocation = "countryCode" in given || "latitude" in given || "longitude" in given;
    const location = touchesLocation
        ? validateCatalogLocation(merged.countryCode, merged.latitude, merged.longitude)
        : { countryCode: item.countryCode, latitude: item.latitude, longitude: item.longitude };
    const next = {
        name: core.name,
        slug: core.slug,
        shortDescription: core.shortDescription,
        description: core.description,
        addressText: core.addressText,
        locality: core.locality,
        region: core.region,
        countryCode: location.countryCode,
        latitude: location.latitude,
        longitude: location.longitude,
        timeZone: core.timeZone,
        fulfilmentMode: fulfilment.fulfilmentMode,
        externalUrl: fulfilment.externalUrl,
        editorialPriority: core.editorialPriority,
    };
    const changed = catalogContentFields.filter((field) => !sameValue(next[field], item[field]));
    if (changed.length === 0)
        throw new DomainValidationError("patch", "does not change the item");
    const patch = {};
    for (const field of changed)
        patch[field] = next[field];
    const has = (fields) => changed.some((field) => fields.includes(field));
    if (has(["slug"]) && item.firstPublishedAt !== null)
        throw violation("catalog.slug_frozen");
    if (has(["fulfilmentMode"]) && item.priceAuthoritative) {
        throw violation("catalog.price_fulfilment_not_payable");
    }
    return Object.freeze({
        patch: patch,
        textChanged: has(TEXT_FIELDS),
        locationChanged: has(LOCATION_FIELDS),
        fulfilmentChanged: has(["fulfilmentMode", "externalUrl"]),
        slugChanged: has(["slug"]),
        editorialChanged: has(["editorialPriority"]),
        changedFieldCount: changed.length,
    });
}
function pick(item) {
    return {
        name: item.name,
        slug: item.slug,
        shortDescription: item.shortDescription,
        description: item.description,
        addressText: item.addressText,
        locality: item.locality,
        region: item.region,
        countryCode: item.countryCode,
        latitude: item.latitude,
        longitude: item.longitude,
        timeZone: item.timeZone,
        fulfilmentMode: item.fulfilmentMode,
        externalUrl: item.externalUrl,
        editorialPriority: item.editorialPriority,
    };
}
function definedOnly(patch) {
    return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined));
}
/**
 * The full subtype of the item's own type. It must match the item type, each field is judged by the
 * draft's rule, and a subtype that changes nothing is refused.
 */
export function planCatalogSubtypeEdit(input) {
    gate(input.item, input);
    const subtype = validateCatalogSubtype(input.item.itemType, input.subtype);
    const current = input.current;
    const next = subtype;
    const changedFieldCount = Object.keys(next).filter((key) => !sameDeep(next[key], current === null ? undefined : current[key])).length;
    if (changedFieldCount === 0)
        throw new DomainValidationError("subtype", "does not change the item");
    return Object.freeze({ subtype, changedFieldCount });
}
function sameDeep(a, b) {
    if (a instanceof Date || b instanceof Date)
        return sameValue(a, b);
    if (typeof a === "object" && a !== null && typeof b === "object" && b !== null) {
        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        return [...keys].every((key) => sameDeep(a[key], b[key]));
    }
    return a === b;
}
/** A replacement of the category set: at most one primary, no repeat. The order is not stored. */
export function planCatalogCategoryReplace(input) {
    gate(input.item, input);
    const categories = validateCatalogCategories(input.categories);
    const key = (list) => [...list]
        .map((entry) => `${entry.code}:${entry.isPrimary}`)
        .sort()
        .join("|");
    if (key(categories) === key(input.current)) {
        throw new DomainValidationError("categories", "does not change the item");
    }
    return Object.freeze({
        categories,
        countBefore: input.current.length,
        countAfter: categories.length,
    });
}
export function planCatalogTagReplace(input) {
    gate(input.item, input);
    const tags = validateCatalogTags(input.tags);
    const key = (list) => [...list]
        .map((entry) => `${entry.namespace}:${entry.code}`)
        .sort()
        .join("|");
    if (key(tags) === key(input.current)) {
        throw new DomainValidationError("tags", "does not change the item");
    }
    return Object.freeze({ tags, countBefore: input.current.length, countAfter: tags.length });
}
/** The order of the list is the order on the page, so a reorder is a change. */
export function planCatalogPeopleReplace(input) {
    gate(input.item, input);
    const people = validateCatalogPeople(input.people);
    const key = (list) => list.map((entry) => `${entry.slug}:${entry.role}`).join("|");
    if (key(people) === key(input.current)) {
        throw new DomainValidationError("people", "does not change the item");
    }
    return Object.freeze({ people, countBefore: input.current.length, countAfter: people.length });
}
/**
 * A replacement of the media list. The list is the order on the page (position is the index), at
 * most one cover and it is an image, no file twice. A path that is not stored yet is an upload that
 * the use case verifies before it writes (`checkNewMediaPath`).
 */
export function planCatalogMediaReplace(input) {
    gate(input.item, input);
    const media = validateCatalogMedia(input.media);
    const stored = new Set(input.current.map((entry) => entry.storageObjectPath));
    const next = new Set(media.map((entry) => entry.storageObjectPath));
    const addedPaths = media
        .map((entry) => entry.storageObjectPath)
        .filter((path) => !stored.has(path));
    const removedPaths = input.current
        .map((entry) => entry.storageObjectPath)
        .filter((path) => !next.has(path));
    const key = (list) => list
        .map((entry) => [
        entry.storageObjectPath,
        entry.kind,
        entry.altText,
        entry.caption,
        entry.credit,
        entry.isCover,
    ].join("\u0000"))
        .join("\u0001");
    if (key(media) === key(input.current)) {
        throw new DomainValidationError("media", "does not change the item");
    }
    const coverBefore = input.current.find((entry) => entry.isCover)?.storageObjectPath ?? null;
    const coverAfter = media.find((entry) => entry.isCover)?.storageObjectPath ?? null;
    return Object.freeze({
        media,
        addedPaths,
        removedPaths,
        countBefore: input.current.length,
        countAfter: media.length,
        addedCount: addedPaths.length,
        removedCount: removedPaths.length,
        coverChanged: coverBefore !== coverAfter,
    });
}
/** One provider reference. Removing one is a later issue (CHT-709), and a repeat is refused by the database. */
export function planCatalogExternalRefAdd(input) {
    gate(input.item, input);
    const [ref] = validateCatalogExternalRefs([input.ref]);
    if (ref === undefined)
        throw new DomainValidationError("externalRefs", "is empty");
    if (input.current.some((entry) => entry.provider === ref.provider)) {
        throw violation("catalog.external_ref_taken");
    }
    return Object.freeze({ ref });
}
/** The verification stamp: only the common checks. The use case sets the time and the actor. */
export function planCatalogVerification(input) {
    gate(input.item, input);
}
// --- media uploads -----------------------------------------------------------------------------------
/** What the browser says about the file it wants to upload: one of the three image types, 1 byte to 10 MiB. */
export function validateCatalogUploadRequest(input) {
    if (!isMember(Object.keys(catalogUploadContentTypes), input.contentType)) {
        throw new DomainValidationError("contentType", "must be a JPEG, PNG or WebP image");
    }
    const { byteSize } = input;
    if (typeof byteSize !== "number" ||
        !Number.isInteger(byteSize) ||
        byteSize < 1 ||
        byteSize > CATALOG_UPLOAD_MAX_BYTES) {
        throw new DomainValidationError("byteSize", `must be a whole number from 1 to ${CATALOG_UPLOAD_MAX_BYTES}`);
    }
    return { contentType: input.contentType, byteSize };
}
/** The image type that a file's first bytes say it is, or `null`. JPEG, PNG and WebP only. */
export function detectCatalogImageType(bytes) {
    const at = (index) => bytes[index];
    if (bytes.length >= 3 && at(0) === 0xff && at(1) === 0xd8 && at(2) === 0xff)
        return "image/jpeg";
    const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (bytes.length >= 8 && png.every((value, index) => at(index) === value))
        return "image/png";
    const ascii = (start, text) => bytes.length >= start + text.length &&
        [...text].every((char, index) => at(start + index) === char.charCodeAt(0));
    if (ascii(0, "RIFF") && ascii(8, "WEBP"))
        return "image/webp";
    return null;
}
/**
 * Judges a path that is new to an item, before the transaction: it must be a key made for THIS item
 * (`catalog/<itemId>/<uploadId>.<ext>`: another item's file, an arbitrary string and a path that
 * climbs are refused), and its upload id must be a UUIDv7 made within the attach window (so an old
 * or invented key is refused, and a pending upload is never older than the orphan sweep window).
 * Returns the content type its extension names. Messages name a field and a rule, never a path.
 */
export function checkNewMediaPath(input) {
    const fail = () => new DomainValidationError("media", "has a file that was not uploaded for this item");
    if (!input.path.startsWith(catalogMediaPrefix(input.catalogItemId)))
        throw fail();
    const parsed = parseCatalogMediaObjectPath(input.path);
    if (parsed === null || parsed.catalogItemId !== input.catalogItemId)
        throw fail();
    const madeAt = uuidV7Instant(parsed.uploadId);
    if (madeAt === null)
        throw fail();
    const age = input.now.getTime() - madeAt.getTime();
    // A little skew into the future is tolerated; an old key is not.
    if (age > CATALOG_MEDIA_ATTACH_WINDOW_MS || age < -5 * 60 * 1000) {
        throw new DomainValidationError("media", "has an upload that has expired");
    }
    const contentType = Object.keys(catalogUploadContentTypes).find((type) => catalogUploadContentTypes[type] === parsed.extension);
    if (contentType === undefined)
        throw fail();
    return contentType;
}
/**
 * Judges what the storage reports about an uploaded file: it exists, it is not empty or too large,
 * its stored type is the one its key names, and its first bytes agree. Throws a validation error
 * (422). Names the rule, never the bytes.
 */
export function assertUploadedMedia(expected, inspection) {
    const fail = (rule) => new DomainValidationError("media", rule);
    if (!inspection.exists)
        throw fail("has a file that was not found in storage");
    if (inspection.byteSize < 1 || inspection.byteSize > CATALOG_UPLOAD_MAX_BYTES) {
        throw fail("has a file that is empty or larger than 10 MiB");
    }
    if (inspection.contentType.split(";")[0]?.trim().toLowerCase() !== expected) {
        throw fail("has a file whose type is not the type of its name");
    }
    if (detectCatalogImageType(inspection.leadingBytes) !== expected) {
        throw fail("has a file whose content is not the image type it claims");
    }
}
